import type { ReactNode } from 'react'
import { Box, Button, Stack, Typography } from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import DownloadIcon from '@mui/icons-material/Download'
import SectionHeader from '../SectionHeader'
import {
  Q1_DIST,
  Q1_HEADLINE_PCT,
  Q2_HEADLINE_PCT,
  Q2_OPTIONS,
  Q3_NPS,
  Q4_HEADLINE_PCT,
  Q4_WORDS,
} from './resultsMockData'

/** Per-question result cards: scale histogram, single-choice bars, NPS, word cloud. */
export default function QuestionResults() {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('results')

  return (
    <Box sx={{ bgcolor: c.white, border: `1px solid ${c.ink08}`, borderRadius: '18px', p: 2.75, display: 'flex', flexDirection: 'column', gap: 2.25 }}>
      <SectionHeader
        title={t('questions.title')}
        subtitle={t('questions.sub')}
        right={
          <Button startIcon={<DownloadIcon sx={{ fontSize: 14 }} />} sx={{ color: c.ink56, fontSize: 12.5, textTransform: 'none', fontWeight: 600 }}>
            {t('questions.exportRaw')}
          </Button>
        }
      />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.25 }}>
        <Q1Card />
        <Q2Card />
        <Q3Card />
        <Q4Card />
      </Box>
    </Box>
  )
}

function QCard({ children }: { children: ReactNode }) {
  const theme = useTheme()
  const c = theme.affexy.colors
  return (
    <Box sx={{ p: 2.25, bgcolor: c.white, border: `1px solid ${c.ink08}`, borderRadius: '14px', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {children}
    </Box>
  )
}

function QHeader({ n, text, type }: { n: number; text: string; type: string }) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('results')
  return (
    <Stack spacing={0.5}>
      <Typography sx={{ fontSize: 11, letterSpacing: '.06em', textTransform: 'uppercase', color: c.ink48, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
        {t('questions.label', { n, type })}
      </Typography>
      <Typography sx={{ fontFamily: theme.affexy.fonts.heading, fontWeight: 700, fontSize: 15, color: c.ink, lineHeight: 1.25 }}>
        {text}
      </Typography>
    </Stack>
  )
}

function Headline({ pct, text }: { pct: string; text: string }) {
  const theme = useTheme()
  const c = theme.affexy.colors
  return (
    <Box sx={{ p: 1.5, bgcolor: alpha(c.coral, 0.05), border: `1px solid ${alpha(c.coral, 0.18)}`, borderRadius: '10px', display: 'flex', alignItems: 'center', gap: 1.25 }}>
      <Typography sx={{ fontWeight: 700, fontSize: 16, color: c.coral, fontVariantNumeric: 'tabular-nums' }}>{pct}</Typography>
      <Typography sx={{ fontSize: 12.5, color: c.ink72, lineHeight: 1.4 }}>{text}</Typography>
    </Box>
  )
}

function Q1Card() {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('results')
  const max = Math.max(...Q1_DIST)
  return (
    <QCard>
      <QHeader n={1} text={t('questions.q1Text')} type={t('questions.scaleType')} />
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, height: 140 }}>
        {Q1_DIST.map((v, i) => {
          const isPeak = i === 3
          return (
            <Stack key={i} alignItems="center" spacing={0.75} sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 12, color: isPeak ? c.coral : c.ink56, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{v}</Typography>
              <Box sx={{ width: '100%', height: `${(v / max) * 100}%`, bgcolor: isPeak ? c.coral : c.gray300, borderRadius: '8px', minHeight: 8 }} />
              <Typography sx={{ fontSize: 12, color: c.ink, fontWeight: 700 }}>{i + 1}</Typography>
            </Stack>
          )
        })}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: c.ink56 }}>
        <span>{t('questions.q1Low')}</span>
        <span>{t('questions.q1High')}</span>
      </Box>
      <Headline pct={Q1_HEADLINE_PCT} text={t('questions.q1Headline')} />
    </QCard>
  )
}

function Q2Card() {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('results')
  const total = Q2_OPTIONS.reduce((a, b) => a + b.n, 0)
  const max = Math.max(...Q2_OPTIONS.map((o) => o.n))
  return (
    <QCard>
      <QHeader n={2} text={t('questions.q2Text')} type={t('questions.q2Type')} />
      <Stack spacing={1}>
        {Q2_OPTIONS.map((o, i) => (
          <Box key={o.labelKey} sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Typography sx={{ fontSize: 12.5, color: c.ink, flex: '0 0 110px' }}>{t(`questions.${o.labelKey}`)}</Typography>
            <Box sx={{ flex: 1, height: 22, bgcolor: c.gray100, borderRadius: '6px', overflow: 'hidden' }}>
              <Box sx={{ width: `${(o.n / max) * 100}%`, height: '100%', bgcolor: i === 0 ? c.coral : i < 3 ? c.coral300 : c.gray300, borderRadius: '6px' }} />
            </Box>
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: c.ink, flex: '0 0 50px', textAlign: 'right' }}>
              {Math.round((o.n / total) * 100)}%
            </Typography>
          </Box>
        ))}
      </Stack>
      <Headline pct={Q2_HEADLINE_PCT} text={t('questions.q2Headline')} />
    </QCard>
  )
}

function Q3Card() {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('results')
  const { detractors, passives, promoters } = Q3_NPS
  const total = detractors + passives + promoters
  const nps = Math.round(((promoters - detractors) / total) * 100)
  return (
    <QCard>
      <QHeader n={3} text={t('questions.q3Text')} type={t('questions.q3Type')} />
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.75 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 42, color: c.coral, fontVariantNumeric: 'tabular-nums', letterSpacing: '-.02em', lineHeight: 1 }}>
          {nps}
        </Typography>
        <Stack spacing={0.25}>
          <Typography sx={{ fontSize: 12, color: c.ink56 }}>{t('questions.q3Nps')}</Typography>
          <Typography sx={{ fontSize: 12, color: c.success, fontWeight: 600 }}>{t('questions.q3Delta')}</Typography>
        </Stack>
      </Box>
      <Box sx={{ display: 'flex', height: 14, borderRadius: '8px', overflow: 'hidden' }}>
        <Box sx={{ width: `${(detractors / total) * 100}%`, bgcolor: c.danger }} />
        <Box sx={{ width: `${(passives / total) * 100}%`, bgcolor: c.gray300 }} />
        <Box sx={{ width: `${(promoters / total) * 100}%`, bgcolor: c.success }} />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
        <Box component="span" sx={{ color: c.danger }}>{t('questions.q3Detractors', { n: detractors, pct: Math.round((detractors / total) * 100) })}</Box>
        <Box component="span" sx={{ color: c.ink56 }}>{t('questions.q3Passives', { n: passives, pct: Math.round((passives / total) * 100) })}</Box>
        <Box component="span" sx={{ color: c.success }}>{t('questions.q3Promoters', { n: promoters, pct: Math.round((promoters / total) * 100) })}</Box>
      </Box>
    </QCard>
  )
}

function Q4Card() {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('results')
  return (
    <QCard>
      <QHeader n={4} text={t('questions.q4Text')} type={t('questions.q4Type')} />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        {Q4_WORDS.map((wo) => (
          <Box
            key={wo.w}
            component="span"
            sx={{
              fontFamily: theme.affexy.fonts.heading,
              fontWeight: wo.s > 20 ? 700 : 400,
              fontSize: 12 + wo.s * 0.5,
              color: wo.s > 25 ? c.coral : wo.s > 15 ? c.ink : c.ink56,
              lineHeight: 1,
              px: 1,
              py: 0.5,
            }}
          >
            {wo.w}
          </Box>
        ))}
      </Box>
      <Headline pct={Q4_HEADLINE_PCT} text={t('questions.q4Headline')} />
    </QCard>
  )
}
